import React from 'react';
import { Target, Award, Users, TrendingUp, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, stats } from '../mock';

export const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            About Solid Base Builder
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {companyInfo.tagline}
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1615634364452-8daf3851f2f1"
                alt="Construction site"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Building Trust Since 2009
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Solid Base Builder has been a trusted name in the construction industry for over 
                {companyInfo.yearsExperience} years. We specialize in both residential and commercial 
                projects, delivering exceptional results that stand the test of time.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our commitment to quality craftsmanship, innovative design solutions, and customer 
                satisfaction has helped us build lasting relationships with our clients. Every project 
                we undertake is treated with the same dedication and attention to detail.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                From custom homes to large-scale commercial developments, we bring expertise, 
                professionalism, and a passion for excellence to every build.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide every project we undertake
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Target,
                title: 'Quality First',
                description: 'We never compromise on quality. Every project meets the highest standards of craftsmanship.'
              },
              {
                icon: Award,
                title: 'Excellence',
                description: 'Striving for excellence in every aspect of construction, from planning to execution.'
              },
              {
                icon: Users,
                title: 'Client Focus',
                description: 'Your satisfaction is our priority. We listen, understand, and deliver beyond expectations.'
              },
              {
                icon: TrendingUp,
                title: 'Innovation',
                description: 'Embracing new technologies and methods to deliver efficient, sustainable solutions.'
              }
            ].map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Solid Base Builder?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              'Licensed and Insured Professionals',
              'Transparent Pricing & Contracts',
              'On-Time Project Delivery',
              'Superior Quality Materials',
              'Experienced Team',
              'Comprehensive Warranties',
              '24/7 Customer Support',
              'Sustainable Building Practices',
              'Competitive Pricing'
            ].map((reason, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                <span className="text-lg text-gray-700 font-medium">{reason}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Meet Our Expert Team
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-12">
            Our skilled professionals bring decades of combined experience to every project, 
            ensuring exceptional results and complete client satisfaction.
          </p>
          <div className="bg-blue-50 rounded-2xl p-12">
            <p className="text-2xl text-gray-700 italic">
              "Our team is our greatest asset. Together, we transform visions into reality, 
              one project at a time."
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
